// ============================================================================
// <ReportIssueButton/> — opens the embedded ticket form from the dashboard.
//
// Placement:
//   • Coach  → viveka-ftw   (e.g. the V-Coach dashboard header/nav)
//   • V-Corp → viveka-core  (v-corp-front-end layout) — see TS note at bottom.
//
// What it does:
//   1. On click, POSTs to the token endpoint (session-authenticated) to mint a
//      short-lived signed identity token.
//   2. Opens a modal with the embedded form in an <iframe>.
//   3. On iframe load, postMessage's the identity to the form, pinned to the
//      form's origin (never '*').
//   4. Optionally auto-closes when the form reports a successful submit.
//
// Pairs with: support-ticket-token.js (mint) + ticket-receiver.js (verify) +
//             viveka-ticket-form-corrected.html (the embedded form).
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import './ReportIssueButton.css';

export default function ReportIssueButton({
  platform = 'coach',                         // 'coach' | 'vcorp'
  role = 'coach',                             // 'coach' | 'client' | 'admin' — participants pass 'client'
  formUrl = '/support/report-issue.html',     // host the form same-origin if you can
  tokenEndpoint = '/api/support/ticket-token',
  label = 'Report an issue',
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const identityRef = useRef(null);
  const iframeRef = useRef(null);
  const triggerRef = useRef(null);

  // Pin the form's origin for postMessage (resolves relative URLs against the app).
  const formOrigin = (() => {
    try { return new URL(formUrl, window.location.href).origin; }
    catch { return window.location.origin; }
  })();

  const close = useCallback(() => {
    setOpen(false);
    setError('');
    if (triggerRef.current) triggerRef.current.focus(); // return focus to the trigger
  }, []);

  const openModal = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${tokenEndpoint}?role=${encodeURIComponent(role)}`,
        { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error('auth');
      identityRef.current = await res.json(); // { token, name, email }
      setOpen(true);
    } catch {
      setError("We couldn't open the report form. Please make sure you're signed in.");
    } finally {
      setLoading(false);
    }
  }, [tokenEndpoint]);

  // Hand identity to the form once the iframe has loaded.
  const handleIframeLoad = useCallback(() => {
    const id = identityRef.current;
    const frame = iframeRef.current;
    if (!id || !frame || !frame.contentWindow) return;
    frame.contentWindow.postMessage(
      { type: 'v-ticket-identity', token: id.token, name: id.name, email: id.email, platform, role },
      formOrigin // pinned — never '*'
    );
  }, [formOrigin, platform, role]);

  // Esc to close + auto-close on a successful submit reported by the form.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onMsg = (e) => {
      if (e.origin !== formOrigin) return;                  // only trust the form's origin
      if (e.data && e.data.type === 'v-ticket-submitted') {
        setTimeout(close, 1400);                            // let the user see the thank-you
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('message', onMsg);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('message', onMsg);
    };
  }, [open, close, formOrigin]);

  const theme = platform === 'vcorp' ? 'vcorp' : 'coach';
  const src = `${formUrl}${formUrl.includes('?') ? '&' : '?'}embed=1`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`v-report-btn ${className}`}
        data-theme={theme}
        onClick={openModal}
        disabled={loading}
      >
        {loading ? 'Opening…' : label}
      </button>
      {error && <p className="v-report-error" role="alert">{error}</p>}

      {open && (
        <div
          className="v-ticket-overlay"
          data-theme={theme}
          onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div className="v-ticket-modal" role="dialog" aria-modal="true" aria-label="Report an issue">
            <button type="button" className="v-ticket-close" aria-label="Close" onClick={close}>×</button>
            <iframe
              ref={iframeRef}
              className="v-ticket-frame"
              title="Report an issue"
              src={src}
              onLoad={handleIframeLoad}
            />
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Usage
//   // viveka-ftw — one mount in MainDashboardWrapper covers coach AND client
//   // pages; derive role from the route so participants get client phrasing:
//   const role = location.pathname.startsWith('/v-coach/client/') ? 'client' : 'coach';
//   <ReportIssueButton platform="coach" role={role} formUrl="/support/report-issue.html" />
//
//   // viveka-core (V-Corp) — same component; admins:
//   <ReportIssueButton platform="vcorp" role="admin" formUrl="/support/report-issue.html" />
//
// TypeScript (viveka-core): add a props interface and rename to .tsx —
//   interface Props { platform?: 'coach' | 'vcorp'; formUrl?: string;
//     tokenEndpoint?: string; label?: string; className?: string; }
//   export default function ReportIssueButton(props: Props) { ... }
//
// Cross-origin form? Then the form must allow this app in CSP frame-ancestors,
// and set CONFIG.allowedParentOrigins to this app's origin (see the form).
// ---------------------------------------------------------------------------
